@use "../../styles/variables" as *;
@use "../../styles/mixins" as *;

* {
  box-sizing: border-box;
  padding: 0;
}

:host(:defined) {
  opacity: 1;
}

:host {
  opacity: 0;

  button[buttonType="outlined-button"],
  a[buttonType="outlined-button"],
  button[buttonType="high-width-outlined-button"],
  a[buttonType="high-width-outlined-button"] {
    background-color: transparent;
    border: 1px solid var(--grey);
    color: var(--grey-input-onfocus-text);

    &:disabled {
      color: var(--grey-light);
      border: 1px solid var(--grey-light);
      cursor: not-allowed;
      background-color: transparent;
      opacity: 0.5;
    }
  }


  a[buttonType=outlined-button], a[buttonType=standard-width-button], a[buttonType=high-width-button] {
    text-decoration: none;
  }

  button[buttonType="text-button"],
  a[buttonType="text-button"] {
    text-decoration: underline;
    color: var(--grey);

    &:disabled {
      cursor: not-allowed;
      color: var(--separator);
      opacity: 0.5;
    }
  }

  .textAndIcon {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .twoLineButton {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
  }
}

button[actionType="primary"],
a[actionType="primary"] {
  background-color: var(--ny-red);
}

button[actionType="secondary"],
a[actionType="secondary"] {
  background-color: var(--grey);
}

button[actionType="tertiary"],
a[actionType="tertiary"] {
  background-color: var(--success-green);
}

button, a {
  color: var(--white);
  border-radius: 1.5rem;
  font-family: Roboto, sans-serif;
  font-size: 0.875rem;
  line-height: 1rem;
  letter-spacing: 0.9px;
  text-transform: uppercase;
  height: 2.5rem;
  cursor: pointer;
  font-weight: 400;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    cursor: not-allowed;
    color: var(--grey-text-light);
    background-color: var(--separator);
  }

  @include desktop {
    height: 3rem;
  }

  @include fullhd {
    font-size: 1.125rem;
    padding: 0.75rem 2.25rem;
  }
}

button[buttonType="high-width-button"],
a[buttonType="high-width-button"] {
  padding: 0 2.25rem;
  min-width: 5.75rem;
  border: none;

  @include desktop {
    min-width: 6rem;
  }
}

button[buttonType="standard-width-button"],
a[buttonType="standard-width-button"] {
  padding: 0 1.5rem;
  min-width: 4.5rem;
  border: none;

  @include desktop {
    min-width: 5rem;
  }
}

button[buttonType="standard-width-icon-button"],
a[buttonType="standard-width-icon-button"] {
  padding: 0 1.5rem;
  min-width: 4.5rem;
  border: none;

  ::slotted(svg) {
    fill: var(--white);
    display: block;
    width: 1.125rem;
    height: 1.125rem;
  }

  &:disabled {
    ::slotted(svg) {
      fill: var(--grey-text-light);
    }
  }
}

button[buttonType="two-line-high-width-button"],
a[buttonType="two-line-high-width-button"] {
  min-width: 4.5rem;
  padding: 0 2.25rem;
  border: none;

  .subText {
    font-size: 0.75rem;
    text-transform: lowercase;
    letter-spacing: 0.5px;
    line-height: 1rem;
  }

  @include desktop {
    min-width: 6rem;
    padding: 0 2.25rem;
  }
}

button[buttonType="outlined-button"],
a[buttonType="outlined-button"],
button[buttonType="outlined-toggle-button"],
a[buttonType="outlined-toggle-button"] {
  padding: 0 1.5rem;
  min-width: 4.5rem;

  &:disabled {
    border: 1px solid var(--grey-light);
    cursor: not-allowed;
    background-color: transparent;
    color: var(--grey-light);
  }

  @include desktop {
    padding: 0 1.5rem;
    min-width: 5rem;
  }
}

button[buttonType="outlined-button"],
a[buttonType="outlined-button"],
button[buttonType="outlined-toggle-button"].toggle-off,
a[buttonType="outlined-toggle-button"].toggle-off {
  background-color: transparent;
  color: var(--ny-dimgrey);
  border: 1px solid var(--ny-dimgrey);
}

button[buttonType="outlined-toggle-button"].toggle-on,
a[buttonType="outlined-toggle-button"].toggle-on {
  border: 1px solid transparent;
}

button[buttonType="high-width-outlined-button"],
a[buttonType="high-width-outlined-button"] {
  min-width: 4.5rem;
  padding: 0 1.5rem;

  @include desktop {
    min-width: 6rem;
    padding: 0 2.25rem;
    background-color: transparent;
    border: 1px solid var(--ny-dimgrey);
    color: var(--ny-dimgrey);

    &:disabled {
      border: 1px solid var(--ny-disabled-light-grey);
      cursor: not-allowed;
      background-color: transparent;
      color: var(--ny-disabled-light-grey);
    }
  }
}

button[buttonType="text-button"], a[buttonType="text-button"] {
  padding: 0 .75rem;
  min-width: 4rem;
  background-color: transparent;
  border: none;

  &:disabled {
    background-color: transparent;
    cursor: not-allowed;
    color: var(--ny-disabled-light-grey);
  }

  @include desktop {
    padding: 1rem;
    min-width: 4.5rem;
  }
}

button[buttonType="icon-button"], a[buttonType="icon-button"] {
  background-color: var(--white);
  border: 0;

  &:disabled {
    cursor: not-allowed;
    border-color: var(--ny-disabled-light-grey);
  }
}

/* slotted svg styling */
::slotted(svg[slot="svg"]) {
  fill: var(--grey);
  display: block;
  width: 1.125rem;
  height: 1.125rem;
  margin-right: 1rem;
}

/* disabled slotted svg */
button[buttonType="icon-button"]:disabled ::slotted(svg[slot="svg"]),
a[buttonType="icon-button"]:disabled ::slotted(svg[slot="svg"]) {
  fill: var(--separator);
}

button, a {
  position: relative;
}

.button-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.loading-spinner {
  width: 1.5rem;
  height: 1.5rem;
  border: 3px solid var(--white);
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

:host([isloading]) .button-content {
  opacity: 0.35;
}

:host([theme="dark"]) [buttonType="text-button"] {
  color: var(--white);
}

:host([theme="dark"]) [buttonType="outlined-button"],
:host([theme="dark"]) [buttonType="high-width-outlined-button"] {
  color: var(--white);
  border-color: var(--white);
}